import * as Sentry from '@sentry/vue'
import { ref } from 'vue'
import { t } from '@/i18n/t'
import { applyTenantBrandLocale } from '@/i18n/tenantLocale'
import type { TenantBrand } from '@/types/api'
import { ApiError, api } from '@/utils/apiClient'
import { isMarketingHost, movedTenantUrl } from '@/utils/platformHost'

const tenant = ref<TenantBrand | null>(null)
const loaded = ref(false)
const loading = ref(false)
const error = ref<string | null>(null)
// #1249 — set when /api/public/tenant responds 404. The booking surface
// otherwise renders with `tenant.value === null`, producing an empty
// `<h1>` + "Avtal et møte" card + "Ingen tjenester er tilgjengelige"
// — a half-working page that looks like an operator with zero services
// rather than a missing tenant. App.vue reads this after loadTenant()
// resolves and bounces to the 404 surface so the visitor gets the
// branded "Vi finner ikke siden du leter etter" page instead.
const notFound = ref(false)

// hasOnboardingSlug peeks at the sessionStorage onboarding store the
// same way apiClient's getTenantSlug() does. Local helper rather than
// importing the apiClient one to keep useTenant from pulling in api
// just for a sessionStorage read.
function hasOnboardingSlug(): boolean {
  try {
    const raw = sessionStorage.getItem('stemne_onboarding')
    if (!raw) return false
    return Boolean((JSON.parse(raw) as { subdomain?: string }).subdomain)
  } catch {
    return false
  }
}

function isCssColor(value: string): boolean {
  const color = value.trim()
  if (!color || /[;{}]/.test(color)) return false
  if (typeof CSS !== 'undefined' && typeof CSS.supports === 'function') {
    return CSS.supports('color', color)
  }

  const probe = document.createElement('span')
  probe.style.color = ''
  probe.style.color = color
  return probe.style.color !== ''
}

export function useTenant() {
  async function fetchTenant() {
    // Skip on marketing site (no tenant to load) — UNLESS we're mid-
    // onboarding, in which case the sessionStorage onboarding store
    // carries a slug that the apiClient uses for X-Tenant-Slug. Without
    // this branch, a setup-stage operator on apex (stemne.no, qa.stemne.no,
    // or bare localhost) gets no tenant data on reload — logo + brand
    // name disappear until they're redirected to {slug}.stemne.no.
    //
    // Uses the shared isMarketingHost helper so qa.stemne.no, demo
    // tenants, and any future apex don't fall back to the old hardcoded
    // list. Without this gate we hit /api/public/tenant on apex which
    // returns 404 (tenant not found) and litters the console (#762
    // post-launch fix).
    if (isMarketingHost() && !hasOnboardingSlug()) return

    loading.value = true
    error.value = null

    try {
      tenant.value = await api.get<TenantBrand>('/api/public/tenant')
      loaded.value = true

      // Apply tenant brand as CSS custom properties on :root
      if (tenant.value) {
        // Tell Sentry which tenant this session belongs to. Slug only:
        // it is the one field that turns "some booking page broke" into
        // "{slug}.stemne.no broke" and is already public in the URL.
        // Display name, orgnr, address and logo are deliberately left out
        // — more identifying weight, no extra triage value. Safe no-op
        // when no Sentry client is active (DSN unset in local dev).
        Sentry.setContext('tenant', { slug: tenant.value.slug })

        const root = document.documentElement
        const primaryColor = tenant.value.primary_color
        if (primaryColor && isCssColor(primaryColor)) {
          root.style.setProperty('--tenant-color', primaryColor)
        } else {
          root.style.removeProperty('--tenant-color')
        }
        // The tenant's configured language is the default for
        // end-customer surfaces — without this, an en-US browser got
        // English chrome around a Norwegian operator's services. No-op
        // over an explicit saved choice and on operator surfaces.
        applyTenantBrandLocale(tenant.value.locale)
      }
    } catch (e) {
      // #1249 — distinguish "tenant doesn't exist" (404) from "request
      // failed for other reasons". A 404 means the subdomain has no
      // matching org — render the 404 surface, not a broken booking
      // page. Anything else (5xx, network, auth) is transient/diagnostic
      // and surfaces via the error ref.
      if (e instanceof ApiError && e.status === 404) {
        // Slug-videresending: a renamed workspace answers its retired
        // subdomain with `moved_to`. Forward the visitor to the same
        // page on the current address — old QR codes and bookmarks
        // keep working. movedTenantUrl's loop guard falls through to
        // the ordinary 404 surface on any nonsense payload.
        const movedTo = (e.body as { moved_to?: string } | undefined)?.moved_to
        if (movedTo && typeof window !== 'undefined') {
          const target = movedTenantUrl(window.location, movedTo)
          if (target) {
            window.location.replace(target)
            return
          }
        }
        notFound.value = true
      } else {
        error.value = t('composablesUseTenant.loadError')
        console.error('Failed to load tenant:', e)
      }
    } finally {
      loading.value = false
    }
  }

  // loadTenant is the boot-time idempotent loader — first call fetches,
  // subsequent calls are no-ops while the cached value is valid.
  async function loadTenant() {
    if (loaded.value || loading.value) return
    await fetchTenant()
  }

  // refresh bypasses the loaded-guard for callers that just mutated
  // tenant state (e.g. logo upload, brand profile save) and want the
  // shared store to reflect it immediately. Without this, every other
  // surface using useTenant keeps the stale snapshot until full page
  // reload.
  async function refresh() {
    await fetchTenant()
  }

  return {
    tenant,
    loaded,
    loading,
    error,
    notFound,
    loadTenant,
    refresh,
  }
}
